/*
  DataMigrationPanel — one-off backfill UI for the P0 Cliniko-style
  modules (Medications / Allergies / Medical History). Managers can:

  1. Run a **Preview** — no data is written; the server returns proposed
     insert counts + a sample of each row so the manager can eyeball
     the parsed output.
  2. Run **Apply** — commits. Idempotency guard on the backend means
     re-running never duplicates rows.

  Optional scope: limit to a single client email if you want to test
  the migration on one record first.
*/
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, PlayCircle, CheckCircle2, Loader2, ArrowRight, FileText, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export default function DataMigrationPanel() {
  const [scopeEmail, setScopeEmail] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [drillIn, setDrillIn] = useState(null);  // {label, item}

  async function runPreview() {
    setPreviewing(true);
    setResult(null);
    try {
      const r = await api.post("/data-migration/preview", {
        client_email: scopeEmail.trim() || null,
        limit_per_source: 500,
      });
      setPreview(r.data);
      toast.success("Preview ready");
    } catch (e) {
      toast.error("Preview failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setPreviewing(false);
    }
  }

  async function runApply() {
    if (!preview) return;
    const willInsert =
      preview.summary.medications.insert_count +
      preview.summary.allergies.insert_count +
      preview.summary.history.insert_count;
    if (!confirm(`Insert ${willInsert} rows across Medications / Allergies / Medical History? Re-runs are safe (idempotent).`)) return;
    setApplying(true);
    try {
      const r = await api.post("/data-migration/apply", {
        client_email: scopeEmail.trim() || null,
        limit_per_source: 500,
      });
      setResult(r.data);
      setPreview(null);
      toast.success("Migration applied");
    } catch (e) {
      toast.error("Apply failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  }

  return (
    <Card className="p-4 space-y-4" data-testid="data-migration-panel">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-zinc-950">Backfill from existing PHR &amp; intake data</h3>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Scans past assessments and intake submissions for medication / allergy / condition /
          falls text, then seeds the new structured registers with <code className="text-2xs bg-zinc-100 px-1 rounded">source: &quot;migrated&quot;</code>.
          Preview is always safe — nothing is written until you press Apply. Idempotent by
          (client + primary field): re-runs never create duplicates.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <Label className="text-xs">Limit to a single client (optional)</Label>
          <Input
            value={scopeEmail}
            onChange={(e) => setScopeEmail(e.target.value)}
            placeholder="rafael@example.com — leave blank to scan every client"
            className="mt-1"
            data-testid="dm-scope-email"
          />
        </div>
        <Button
          variant="outline"
          onClick={runPreview}
          disabled={previewing || applying}
          data-testid="dm-preview-btn"
        >
          {previewing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
          Preview
        </Button>
        <Button
          onClick={runApply}
          disabled={!preview || applying}
          data-testid="dm-apply-btn"
        >
          {applying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5 mr-1.5" />}
          Apply
        </Button>
      </div>

      {preview && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-3" data-testid="dm-preview-report">
          <div className="text-xs font-semibold text-indigo-900 uppercase tracking-wider flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Preview — nothing written yet
          </div>
          <SummaryGrid summary={preview.summary} />
          <SampleList label="Medications" items={preview.sample.medications} primary="name" testId="dm-sample-med" onDrillIn={setDrillIn} />
          <SampleList label="Allergies" items={preview.sample.allergies} primary="substance" testId="dm-sample-alg" onDrillIn={setDrillIn} />
          <SampleList label="Medical History" items={preview.sample.history} primary="title" testId="dm-sample-hist" onDrillIn={setDrillIn} />
        </div>
      )}

      <DrillInDialog drillIn={drillIn} onClose={() => setDrillIn(null)} />

      {result && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50/60 p-3 space-y-1" data-testid="dm-apply-result">
          <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Applied
          </div>
          <div className="text-sm text-emerald-950">
            Inserted <b>{result.inserted.medications}</b> medications,{" "}
            <b>{result.inserted.allergies}</b> allergies,{" "}
            <b>{result.inserted.history}</b> history entries.
          </div>
          <div className="text-2xs text-emerald-800">
            Skipped {result.skipped.medications + result.skipped.allergies + result.skipped.history} duplicates
            (already existed for that client).
          </div>
        </div>
      )}
    </Card>
  );
}

function SummaryGrid({ summary }) {
  const rows = [
    { key: "medications", label: "Medications" },
    { key: "allergies",   label: "Allergies" },
    { key: "history",     label: "Medical History" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2 text-center text-sm">
      {rows.map((r) => (
        <div key={r.key} className="rounded-md border border-indigo-200 bg-white p-2" data-testid={`dm-summary-${r.key}`}>
          <div className="text-2xs uppercase tracking-wider text-indigo-700">{r.label}</div>
          <div className="text-lg font-bold text-indigo-950 font-mono-num">
            {summary[r.key].insert_count}
          </div>
          <div className="text-2xs text-indigo-800/80">
            new · <span className="text-indigo-700/60">{summary[r.key].skip_count} already exist</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SampleList({ label, items, primary, testId, onDrillIn }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="text-2xs uppercase tracking-wider text-indigo-800 font-semibold">
        {label} — first {items.length}
      </div>
      <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto pr-1">
        {items.map((it, i) => {
          const refs = it.source_refs || [];
          const clickable = refs.length > 0;
          return (
            <li
              key={i}
              className={
                "flex items-start gap-2 py-0.5 rounded px-1 -mx-1 " +
                (clickable ? "cursor-pointer hover:bg-indigo-100/60" : "")
              }
              onClick={() => clickable && onDrillIn?.({ label, primary, item: it })}
              data-testid={`${testId}-row-${i}`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={(e) => {
                if (clickable && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onDrillIn?.({ label, primary, item: it });
                }
              }}
            >
              <span className="text-indigo-500 font-mono-num shrink-0 min-w-[1.5rem]">·</span>
              <span className="text-zinc-500 shrink-0 truncate max-w-[200px]" title={it.client_email}>
                {it.client_email}
              </span>
              <span className="text-zinc-400">→</span>
              <span className="text-zinc-900 flex-1 truncate">{it[primary]}</span>
              {refs.length > 0 && (
                <span
                  className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-indigo-200 text-indigo-700 flex items-center gap-1"
                  title="Click to see the source excerpt"
                >
                  <FileText className="h-3 w-3" />
                  {refs.length}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function DrillInDialog({ drillIn, onClose }) {
  if (!drillIn) return null;
  const { label, primary, item } = drillIn;
  const refs = item.source_refs || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[92vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="dm-drill-in-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-700" />
            {label} · <span className="text-zinc-900">{item[primary]}</span>
          </DialogTitle>
          <DialogDescription>
            {item.client_email} · {refs.length} source{refs.length === 1 ? "" : "s"} contributed to this proposal
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {refs.map((ref, i) => (
            <div key={i} className="border border-zinc-200 rounded p-3 bg-white" data-testid={`dm-drill-src-${i}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className="text-2xs uppercase border bg-indigo-50 text-indigo-800 border-indigo-200">
                  {ref.kind || "source"}
                </Badge>
                {ref.template_name && (
                  <span className="text-xs text-zinc-700 font-medium">{ref.template_name}</span>
                )}
                {ref.field_label && (
                  <span className="text-2xs text-zinc-500">Field: {ref.field_label}</span>
                )}
                {(ref.started_at || ref.submitted_at) && (
                  <span className="text-2xs text-zinc-400 ml-auto">
                    {new Date(ref.started_at || ref.submitted_at).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mb-1">Parsed line:</div>
              <div className="text-sm font-medium bg-yellow-100 text-zinc-900 px-2 py-1 rounded mb-2">
                {ref.snippet || item[primary]}
              </div>
              {ref.raw && (
                <>
                  <div className="text-xs text-zinc-500 mb-1">Full field content:</div>
                  <pre className="text-xs text-zinc-800 bg-zinc-50 border border-zinc-200 rounded p-2 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {highlightSnippet(ref.raw, ref.snippet || item[primary])}
                  </pre>
                </>
              )}
              {ref.id && (
                <div className="text-2xs text-zinc-400 mt-1 font-mono-num flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  {ref.kind} · {ref.id.slice(0, 10)}…
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function highlightSnippet(raw, snippet) {
  if (!snippet || !raw) return raw;
  const idx = raw.toLowerCase().indexOf(snippet.toLowerCase());
  if (idx < 0) return raw;
  return [
    raw.slice(0, idx),
    <mark key="m" className="bg-yellow-200 px-0.5">{raw.slice(idx, idx + snippet.length)}</mark>,
    raw.slice(idx + snippet.length),
  ];
}
